// packages/web/src/utils/apiBase.ts
function stripTrailingSlash(p: string) {
  return p.replace(/\/+$/, '');
}

function ensureApiSuffix(p: string) {
  const n = stripTrailingSlash(p);
  return /\/api$/i.test(n) ? n : `${n}/api`;
}

function isLocalHostname(h: string) {
  return h === 'localhost' || h === '127.0.0.1' || h.endsWith('.local');
}

/**
 * Resolves the browser-facing API base URL.
 *
 * Rules:
 *  - If NEXT_PUBLIC_API_URL (or API_BASE) is provided, use it when valid:
 *      • '/api' or '/some/base' (relative) -> use as-is (normalized)
 *      • absolute http(s) URL -> use origin + path (normalized)
 *      • anything else -> ignored
 *  - In the browser:
 *      • If port === '3011' OR hostname is local -> map to :3010 (standardized ports)
 *      • Otherwise -> same-origin '/api' (let the reverse-proxy route it)
 *  - On SSR:
 *      • production -> '/api'
 *      • non-production -> 'http://localhost:3010/api' (standardized ports)
 */
const DEBUG = process.env.NODE_ENV !== 'production';

export function getApiBaseUrl(): string {
  const RAW = (process.env.NEXT_PUBLIC_API_URL || process.env.API_BASE || '').trim();

  if (DEBUG) {
    console.log('[API_BASE][getApiBaseUrl] ===== API BASE URL DEBUG =====');
    console.log('[API_BASE][getApiBaseUrl] NEXT_PUBLIC_API_URL:', process.env.NEXT_PUBLIC_API_URL);
    console.log('[API_BASE][getApiBaseUrl] API_BASE:', process.env.API_BASE);
    console.log('[API_BASE][getApiBaseUrl] RAW (trimmed):', RAW);
    console.log('[API_BASE][getApiBaseUrl] NODE_ENV:', process.env.NODE_ENV);
    console.log('[API_BASE][getApiBaseUrl] typeof window:', typeof window);
  }

  if (typeof window !== 'undefined') {
    const { protocol, hostname, port } = window.location;
    const localHost = isLocalHostname(hostname) || port === '3011';

    if (DEBUG) {
      console.log('[API_BASE][getApiBaseUrl] Browser environment detected');
      console.log('[API_BASE][getApiBaseUrl] protocol:', protocol);
      console.log('[API_BASE][getApiBaseUrl] hostname:', hostname);
      console.log('[API_BASE][getApiBaseUrl] port:', port);
      console.log('[API_BASE][getApiBaseUrl] localHost:', localHost);
    }

    // If RAW exists but points to localhost while we're on a non-local host, ignore it.
    if (RAW) {
      if (DEBUG) console.log('[API_BASE][getApiBaseUrl] RAW exists, processing...');
      try {
        if (RAW.startsWith('/')) {
          const result = ensureApiSuffix(RAW);
          if (DEBUG) console.log('[API_BASE][getApiBaseUrl] RAW starts with /, returning:', result);
          return result;
        }
        const u = new URL(RAW);
        const rawIsLocal = isLocalHostname(u.hostname);
        if (DEBUG) {
          console.log('[API_BASE][getApiBaseUrl] Parsed URL:', u.href);
          console.log('[API_BASE][getApiBaseUrl] rawIsLocal:', rawIsLocal);
        }
        if (!localHost && rawIsLocal) {
          if (DEBUG)
            console.log('[API_BASE][getApiBaseUrl] Ignoring localhost URL on non-local host');
          // ignore baked dev URL; fall through to runtime mapping
        } else {
          const result = ensureApiSuffix(`${u.origin}${stripTrailingSlash(u.pathname)}`);
          if (DEBUG) console.log('[API_BASE][getApiBaseUrl] Using RAW URL, returning:', result);
          return result;
        }
      } catch (error) {
        if (DEBUG) console.log('[API_BASE][getApiBaseUrl] Error parsing RAW URL:', error);
        // invalid env; ignore and fall through
      }
    }

    // Runtime mapping
    if (DEBUG) console.log('[API_BASE][getApiBaseUrl] Using runtime mapping');
    if (localHost) {
      const result = `${protocol}//${hostname}:3010/api`;
      if (DEBUG) console.log('[API_BASE][getApiBaseUrl] Local host detected, returning:', result);
      return result;
    }

    const result = '/api';
    if (DEBUG) console.log('[API_BASE][getApiBaseUrl] Non-local host, returning:', result);
    return result;
  }

  // SSR
  if (DEBUG) console.log('[API_BASE][getApiBaseUrl] SSR environment detected');
  if (RAW) {
    if (DEBUG) console.log('[API_BASE][getApiBaseUrl] RAW exists in SSR, processing...');
    try {
      if (RAW.startsWith('/')) {
        const result = ensureApiSuffix(RAW);
        if (DEBUG)
          console.log('[API_BASE][getApiBaseUrl] SSR RAW starts with /, returning:', result);
        return result;
      }
      const u = new URL(RAW);
      const result = ensureApiSuffix(`${u.origin}${stripTrailingSlash(u.pathname)}`);
      if (DEBUG) console.log('[API_BASE][getApiBaseUrl] SSR using RAW URL, returning:', result);
      return result;
    } catch (error) {
      if (DEBUG) console.log('[API_BASE][getApiBaseUrl] SSR error parsing RAW URL:', error);
    }
  }
  const result = process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3010/api';
  if (DEBUG) {
    console.log('[API_BASE][getApiBaseUrl] SSR fallback, returning:', result);
    console.log('[API_BASE][getApiBaseUrl] ===== API BASE URL DEBUG END =====');
  }
  return result;
}
